/**
 * Gallery Masonry — Client-side interactivity
 *
 * Architecture: Progressive Enhancement
 *   - Layer 1: Liquid renders ALL cards server-side (zero latency, SEO-friendly)
 *   - Layer 2: JS reads existing DOM cards for filtering / sorting / pagination / modal
 *   - Layer 3: If Liquid was truncated (Shopify limits), JS fetches remaining
 *              entries via Storefront GraphQL API — silently, in background,
 *              non-blocking. No token? No problem — just use what Liquid gave us.
 */
class GalleryMasonrySection {
  constructor(sectionEl) {
    this.section = sectionEl;
    this.sectionId = sectionEl.dataset.sectionId;

    // Parse config from JSON script tag (injected by Liquid)
    const configEl = document.getElementById('GalleryConfig-' + this.sectionId);
    if (configEl) {
      try { this.config = JSON.parse(configEl.textContent); }
      catch (e) { this.config = {}; }
    } else {
      this.config = {};
    }
    this.pageSize      = this.config.pageSize || 12;
    this.headingSize   = this.config.headingSize || 'title-md';
    this.apiDomain     = this.config.domain || '';
    this.apiToken      = this.config.storefrontAccessToken || '';
    this.liquidTotal   = this.config.liquidTotal || 0;

    // DOM refs
    this.masonryGrid   = sectionEl;
    this.cards         = [];                       // filled in init() from existing DOM
    this.filterBar     = document.getElementById('GalleryFilterBar-' + this.sectionId);
    this.paginationEl  = document.getElementById('GalleryPagination-' + this.sectionId);
    this.noResultsEl   = document.getElementById('GalleryNoResults-' + this.sectionId);
    this.modal         = document.getElementById('GalleryModal-' + this.sectionId);
    this.modalBody     = this.modal?.querySelector('[data-modal-body]');

    // State
    this.state = {
      character: '',
      tag: '',
      sort: 'random',
      page: 1
    };

    this.filteredCards      = [];
    this.totalPages         = 1;
    this.currentOpenCard    = null;
    this.videoObserver      = null;

    // Random sort: only reshuffle on filter/sort-to-random change
    this.lastRandomKey       = '';
    this.randomSeedVersion   = 0;

    // Hash prefix to avoid collisions
    this.hashPrefix = 'gm_' + this.sectionId + '_';

    this.init();
  }

  /* ================================================================
     Init — Reads Liquid-rendered cards from DOM immediately.
     If truncated, optionally fetches remaining in background.
     ================================================================ */
  async init() {
    this.readHash();
    this.bindFilterEvents();
    this.bindPaginationEvents();
    this.bindCardDots();

    // --- Layer 1 & 2: Cards are already in the DOM from Liquid ---
    this.cards = Array.from(this.masonryGrid.querySelectorAll('.gallery-card'));

    const rendered = this.cards.length;

    console.log(
      '[Gallery Masonry] Liquid rendered ' + rendered +
      ' cards (liquidTotal=' + this.liquidTotal + ', token=' +
      (this.apiToken ? 'yes(len=' + this.apiToken.length + ')' : 'no') + ')'
    );

    // Interactive features — ready immediately, no waiting
    this.populateFilters();
    this.bindModalEvents();
    this.bindResizeEvents();
    this.initVideoObserver();
    this.applyAll();
    this.listenHashChange();

    // --- Layer 3: Detect truncation → fetch remaining via API ---
    //
    // Shopify Liquid limits metaobject loops to 50 entries.
    // Two signals indicate truncation:
    //   a) liquidTotal > rendered   (reliable when size filter isn't limited)
    //   b) rendered === 50          (heuristic: exact hit on known limit)
    //
    var wasTruncated = false;
    if (this.liquidTotal > 0 && rendered < this.liquidTotal) {
      wasTruncated = true;
    } else if (rendered >= 50) {
      // If exactly 50 cards AND liquidTotal also says 50, it's almost
      // certainly truncated (chance of having exactly 50 entries is low)
      wasTruncated = true;
    }

    if (wasTruncated && this.apiDomain && this.apiToken) {
      console.log('[Gallery Masonry] Likely truncated! Fetching additional entries via GraphQL...');
      var newEntries = await this.loadRemainingEntries();
      if (newEntries && newEntries.length > 0) {
        this.appendNewCards(newEntries);
        this.cards = Array.from(this.masonryGrid.querySelectorAll('.gallery-card'));
        this.populateFilters();   // re-populate dropdowns with full set
        this.bindModalEvents();   // re-bind expand buttons for new cards
        this.applyAll();
        console.log('[Gallery Masonry] API fetched ' + newEntries.length + ' additional entries. Total: ' + this.cards.length);
      } else if (newEntries === null) {
        console.warn('[Gallery Masonry] API fallback failed. Showing ' + rendered + ' cards from Liquid only.');
      } else {
        console.log('[Gallery Masonry] API returned 0 new entries. All already rendered by Liquid.');
      }
    } else if (wasTruncated && (!this.apiDomain || !this.apiToken)) {
      console.warn(
        '[Gallery Masonry] Truncation detected (' + rendered + ' cards) but NO Storefront API token configured. ' +
        'Go to Theme Editor → Gallery Masonry → "Storefront API Access Token" to unlock 50+ entries.'
      );
    }
  }

  /* ================================================================
     API Layer — only called when Liquid was truncated
     ================================================================ */
  async loadRemainingEntries() {
    const allEntries = [];
    let cursor = null;
    let hasNext = true;

    try {
      while (hasNext) {
        const query = `
          query($first: Int!, $after: String) {
            metaobjects(first: $first, after: $after, type: "gallery") {
              nodes {
                id
                handle
                fields {
                  key
                  value
                  reference {
                    ... on MediaImage {
                      image { url width height altText }
                    }
                    ... on Video {
                      sources { url format }
                      previewImage { url width height }
                    }
                    ... on Product {
                      title
                      id
                      handle
                    }
                  }
                  references(first: 10) {
                    nodes {
                      ... on MediaImage {
                        image { url width height altText }
                      }
                      ... on Video {
                        sources { url format }
                        previewImage { url width height }
                      }
                      ... on Product {
                        title
                        id
                        handle
                      }
                    }
                  }
                }
              }
              pageInfo { hasNextPage endCursor }
            }
          }
        `;

        const endpoint = 'https://' + this.apiDomain + '/api/2025-04/graphql.json';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.apiToken
          },
          body: JSON.stringify({ query: query, variables: { first: 250, after: cursor } })
        });

        if (!res.ok) {
          console.warn('[Gallery Masonry] API request failed (' + res.status + '). ' +
            'Check: 1) Token is correct, 2) Metaobject "gallery" has Storefront access enabled. ' +
            'Using Liquid-rendered cards only.');
          return null;
        }

        const json = await res.json();
        if (json.errors) {
          console.warn('[Gallery Masonry] GraphQL errors:', json.errors);
          for (var i = 0; i < json.errors.length; i++) {
            console.warn('  [' + i + ']', json.errors[i].message || json.errors[i]);
          }
          return null;
        }

        const metaobjects = json.data && json.data.metaobjects;
        if (!metaobjects) return null;

        const nodes = metaobjects.nodes || [];
        for (const node of nodes) {
          const entry = this.parseMetaobject(node);
          if (entry) allEntries.push(entry);
        }

        hasNext = metaobjects.pageInfo.hasNextPage;
        cursor = metaobjects.pageInfo.endCursor;
      }

      // Only return entries not already in DOM (matched by title)
      const existingTitles = new Set();
      this.cards.forEach(function(card) {
        const titleEl = card.querySelector('.gallery-card__title');
        if (titleEl) existingTitles.add(titleEl.textContent.trim());
      });

      return allEntries.filter(function(e) { return !existingTitles.has(e.title); });
    } catch (err) {
      console.warn('[Gallery Masonry] API fetch error:', err.message || err);
      return null;
    }
  }

  parseMetaobject(node) {
    const fields = node.fields || [];
    const byKey = {};
    for (const f of fields) { byKey[f.key] = f; }

    const entry = {
      title: '', story: '', creator: '', url: '',
      tags: [], characters: [],
      date: '', dateTimestamp: 0, dateFormatted: '',
      isVideo: false, videoUrl: '', videoPoster: '',
      videoPosterWidth: 0, videoPosterHeight: 0,
      pictures: [], picCount: 0,
      products: [],
      linkedPage: null,
      linkedCollection: null
    };

    const titleField = byKey['gallery-title'];
    if (titleField) entry.title = titleField.value || '';

    const storyField = byKey['gallery-story'];
    if (storyField) entry.story = storyField.value || '';

    const creatorField = byKey['gallery-creator'];
    if (creatorField) entry.creator = creatorField.value || '';

    const urlField = byKey['gallery-url'];
    if (urlField) entry.url = urlField.value || '';

    // One/Page reference: 关联page (field key: gallery-page)
    const linkedPageField = byKey['gallery-page'];
    if (linkedPageField && linkedPageField.reference) {
      entry.linkedPage = {
        title: linkedPageField.reference.title || '',
        handle: linkedPageField.reference.handle || '',
        url: linkedPageField.reference.onlineStoreUrl || ('/pages/' + (linkedPageField.reference.handle || ''))
      };
    }

    // One/Collection reference: 关联collection (field key: gallery-collection)
    const linkedColField = byKey['gallery-collection'];
    if (linkedColField && linkedColField.reference) {
      entry.linkedCollection = {
        title: linkedColField.reference.title || '',
        handle: linkedColField.reference.handle || '',
        url: linkedColField.reference.onlineStoreUrl || ('/collections/' + (linkedColField.reference.handle || ''))
      };
    }

    const tagField = byKey['gallery-tag'];
    if (tagField && tagField.value) {
      try { entry.tags = JSON.parse(tagField.value); }
      catch (e) { entry.tags = tagField.value.split(',').map(function(t) { return t.trim(); }).filter(Boolean); }
    }

    const charField = byKey['gallery-character'];
    if (charField && charField.value) {
      try { entry.characters = JSON.parse(charField.value); }
      catch (e) { entry.characters = charField.value.split(',').map(function(c) { return c.trim(); }).filter(Boolean); }
    }

    const dateField = byKey['gallery-data'];
    if (dateField && dateField.value) {
      entry.date = dateField.value;
      var ts = Date.parse(entry.date);
      entry.dateTimestamp = isNaN(ts) ? 0 : Math.floor(ts / 1000);
      try {
        var d = new Date(entry.date + 'T00:00:00');
        entry.dateFormatted = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      } catch (e) { entry.dateFormatted = entry.date; }
    }

    // Media — Storefront API 2025-04 returns MediaImage fields directly
    var mediaField = byKey['gallery-media'];
    if (mediaField && mediaField.reference) {
      var ref = mediaField.reference;
      if (ref.sources && ref.sources.length > 0) {
        entry.isVideo = true;
        entry.videoUrl = ref.sources[0].url;
        if (ref.previewImage) {
          entry.videoPoster = ref.previewImage.url;
          entry.videoPosterWidth = ref.previewImage.width || 0;
          entry.videoPosterHeight = ref.previewImage.height || 0;
        }
      } else if (ref.url || (ref.image && ref.image.url)) {
        entry.pictures.push(this.extractImage(ref));
      }
    }

    var picField = byKey['gallery-picture'];
    if (picField && picField.references && picField.references.nodes) {
      var refs = picField.references.nodes;
      for (var i = 0; i < refs.length; i++) {
        entry.pictures.push(this.extractImage(refs[i]));
      }
    }
    entry.picCount = entry.pictures.length;

    // Products — List, Product references
    var productField = byKey['product'];
    if (productField && productField.references && productField.references.nodes) {
      var prodRefs = productField.references.nodes;
      for (var j = 0; j < prodRefs.length; j++) {
        if (prodRefs[j].title) {
          entry.products.push({
            title: prodRefs[j].title,
            id: prodRefs[j].id,
            handle: prodRefs[j].handle || ''
          });
        }
      }
    }

    if (entry.isVideo && !entry.videoPoster && entry.pictures.length > 0) {
      entry.videoPoster = entry.pictures[0].url;
      entry.videoPosterWidth = entry.pictures[0].width;
      entry.videoPosterHeight = entry.pictures[0].height;
    }

    return entry;
  }

  extractImage(node) {
    // Storefront API 2025-04: MediaImage fields are flat (url, width, height)
    if (node.url) {
      return {
        url: node.url || '',
        width: node.width || 0,
        height: node.height || 0
      };
    }
    // Older APIs: MediaImage wraps an Image object
    return {
      url: node.image ? (node.image.url || '') : '',
      width: node.image ? (node.image.width || 0) : 0,
      height: node.image ? (node.image.height || 0) : 0
    };
  }

  cdnUrl(baseUrl, width) {
    if (!baseUrl) return '';
    if (!width) return baseUrl;
    var sep = baseUrl.indexOf('?') >= 0 ? '&' : '?';
    return baseUrl + sep + 'width=' + width;
  }

  /* Append API-fetched cards into the existing DOM grid */
  appendNewCards(newEntries) {
    var self = this;
    newEntries.forEach(function(entry, i) {
      var html = self.renderCardHTML(entry, self.cards.length + i);
      var temp = document.createElement('div');
      temp.innerHTML = html.trim();
      var card = temp.firstElementChild;
      if (card) self.masonryGrid.appendChild(card);
    });
  }

  renderCardHTML(entry, index) {
    var pictures = entry.pictures, isVideo = entry.isVideo,
        videoUrl = entry.videoUrl, videoPoster = entry.videoPoster,
        title = entry.title, characters = entry.characters, tags = entry.tags;

    var imgW = 1, imgH = 1;
    if (isVideo && entry.videoPosterWidth > 0) {
      imgW = entry.videoPosterWidth; imgH = entry.videoPosterHeight;
    } else if (pictures.length > 0) {
      imgW = pictures[0].width || 1; imgH = pictures[0].height || 1;
    } else if (isVideo) {
      imgW = 16; imgH = 9;
    }

    var ratioCss = imgW + ' / ' + imgH;
    var picCount = pictures.length;
    var picUrls = pictures.map(function(p) { return this.cdnUrl(p.url, 800); }.bind(this)).join(',');

    // Media HTML
    var mediaHTML = '';
    if (isVideo && videoUrl) {
      var posterAttr = videoPoster ? ' poster="' + this.escAttr(videoPoster) + '"' : '';
      mediaHTML = '<video class="gallery-card__video" muted loop playsinline preload="metadata" disablepictureinpicture' + posterAttr + '>' +
        '<source src="' + this.escAttr(videoUrl) + '" type="video/mp4"></video>';
    } else if (pictures.length > 0) {
      pictures.forEach(function(pic, i) {
        mediaHTML += '<img src="' + this.escAttr(pic.url || '') + '" alt="' + this.escAttr(title) +
          '" loading="lazy" class="gallery-card__image' + (i === 0 ? ' is-active' : '') + '" data-index="' + i +
          '" sizes="(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw" width="' + (pic.width || 800) +
          '" height="' + (pic.height || 600) + '">';
      }.bind(this));
    } else {
      mediaHTML = '<div class="gallery-card__placeholder bg-[rgb(var(--color-placeholder))] flex items-center justify-center w-full h-full absolute inset-0">' +
        '<svg class="placeholder w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"/><path d="M210.4 299.5h-2.8v-71.7h2.8v71.7zm0-80.2h-2.8v-3h2.8v3z"/></svg></div>';
    }

    // Dot navigation
    var dotsHTML = '';
    if (!isVideo && picCount > 1) {
      dotsHTML = '<div class="gallery-card__dots" data-picture-dots>';
      for (var i = 0; i < picCount; i++) {
        dotsHTML += '<button type="button" class="gallery-card__dot' + (i === 0 ? ' is-active' : '') +
          '" data-dot-index="' + i + '" aria-label="Image ' + (i + 1) + '"></button>';
      }
      dotsHTML += '</div>';
    }

    // Detail template
    var detailMediaUrl = '', detailMediaType = '', detailMediaUrls = '',
        detailPicCount = 0, videoPosterData = '';

    if (isVideo && videoUrl) {
      detailMediaUrl = videoUrl; detailMediaType = 'video';
      if (videoPoster) videoPosterData = ' data-video-poster="' + this.escAttr(videoPoster) + '"';
    } else if (pictures.length > 0) {
      detailMediaUrl = this.cdnUrl(pictures[0].url, 1600); detailMediaType = 'image';
      if (picCount > 1) {
        detailMediaUrls = pictures.map(function(p) { return this.cdnUrl(p.url, 1600); }.bind(this)).join(',');
        detailPicCount = picCount;
      }
    }

    var storyHTML = '';
    if (entry.story) {
      storyHTML = '<div class="gallery-detail__story rte leading-relaxed subtext-md">' + entry.story + '</div>';
    }

    var fieldsHTML = '<div class="gallery-detail__fields">';
    if (entry.creator) fieldsHTML += '<div class="gallery-detail__field"><strong class="gallery-detail__creator">@' + this.escHtml(entry.creator) + '</strong></div>';
    if (entry.linkedPage) {
      fieldsHTML += '<div class="gallery-detail__field"><a href="' + this.escAttr(entry.linkedPage.url) +
        '" class="gallery-detail__link underline" target="_blank" rel="noopener noreferrer">' + this.escHtml(entry.linkedPage.title) + '</a></div>';
    }
    if (entry.linkedCollection) {
      fieldsHTML += '<div class="gallery-detail__field"><a href="' + this.escAttr(entry.linkedCollection.url) +
        '" class="gallery-detail__link underline" target="_blank" rel="noopener noreferrer">' + this.escHtml(entry.linkedCollection.title) + '</a></div>';
    }
    if (entry.products && entry.products.length > 0) {
      for (var pi = 0; pi < entry.products.length; pi++) {
        var p = entry.products[pi];
        var phandle = p.handle || '';
        fieldsHTML += '<div class="gallery-detail__field"><a href="/products/' + this.escAttr(phandle) +
          '" class="gallery-detail__product-link" data-product-handle="' + this.escAttr(phandle) + '">' +
          this.escHtml(p.title) + '</a></div>';
      }
    }
    if (entry.url) {
      var displayUrl = entry.url.replace(/^https?:\/\//, '').substring(0, 60);
      fieldsHTML += '<div class="gallery-detail__field"><a href="' + this.escAttr(entry.url) +
        '" class="gallery-detail__link underline" target="_blank" rel="noopener noreferrer">' + this.escHtml(displayUrl) + '</a></div>';
    }
    if (entry.dateFormatted) fieldsHTML += '<div class="gallery-detail__field"><span class="gallery-detail__value">' + this.escHtml(entry.dateFormatted) + '</span></div>';
    if (tags && tags.length > 0) {
      var tagStr = tags.map(function(t) { return '#' + t; }).join(', ');
      fieldsHTML += '<div class="gallery-detail__field"><span class="gallery-detail__value">' + this.escHtml(tagStr) + '</span></div>';
    }
    fieldsHTML += '</div>';

    var plusIcon = '<svg class="gallery-card__expand-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';

    var charStr = characters ? characters.join(', ') : '';
    var tagStr = tags ? tags.join(', ') : '';

    return '<div class="gallery-card" ' +
      'data-entry-index="' + index + '" ' +
      'data-character="' + this.escAttr(charStr) + '" ' +
      'data-date="' + (entry.dateTimestamp || 0) + '"' +
      (tagStr ? ' data-tags="' + this.escAttr(tagStr) + '"' : '') +
      '>' +
      '<div class="gallery-card__media-wrapper relative overflow-hidden" style="aspect-ratio: ' + ratioCss + ';" data-picture-count="' + picCount + '" data-picture-urls="' + this.escAttr(picUrls) + '">' +
      mediaHTML + dotsHTML +
      '<div class="gallery-card__overlay">' +
      '<h3 class="gallery-card__title heading text-sm lg:text-base leading-tight">' + this.escHtml(title) + '</h3>' +
      '<button type="button" class="gallery-card__expand flex-shrink-0" aria-label="Expand details" data-expand>' + plusIcon + '</button>' +
      '</div></div>' +
      '<template class="gallery-card__detail-template" data-media-url="' + this.escAttr(detailMediaUrl) +
      '" data-media-type="' + detailMediaType + '" data-media-urls="' + this.escAttr(detailMediaUrls) +
      '" data-picture-count="' + detailPicCount + '"' + videoPosterData +
      ' data-media-width="' + imgW + '" data-media-height="' + imgH + '">' +
      '<div class="gallery-detail">' +
      '<h2 class="gallery-detail__title heading ' + this.headingSize + '">' + this.escHtml(title) + '</h2>' +
      storyHTML + fieldsHTML +
      '</div></template></div>';
  }

  /* HTML escape helpers */
  escHtml(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  escAttr(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  /* ================================================================
     Populate Filter Dropdowns — reads from DOM cards' data attrs
     ================================================================ */
  populateFilters() {
    if (!this.filterBar) return;

    var charSelect = this.filterBar.querySelector('[data-filter="character"]');
    var tagSelect  = this.filterBar.querySelector('[data-filter="tag"]');

    // Clear existing dynamically-added options (keep "All *" default)
    if (charSelect) {
      while (charSelect.options.length > 1) charSelect.remove(1);
    }
    if (tagSelect) {
      while (tagSelect.options.length > 1) tagSelect.remove(1);
    }

    var charSet = new Set();
    var tagSet  = new Set();

    this.cards.forEach(function(card) {
      var chars = (card.dataset.character || '').split(',').map(function(c) { return c.trim(); }).filter(Boolean);
      var tags  = (card.dataset.tags || '').split(',').map(function(t) { return t.trim(); }).filter(Boolean);
      chars.forEach(function(c) { charSet.add(c); });
      tags.forEach(function(t) { tagSet.add(t); });
    });

    var chars = Array.from(charSet).sort();
    var tagList = Array.from(tagSet).sort();

    if (charSelect) {
      chars.forEach(function(ch) {
        var opt = document.createElement('option');
        opt.value = ch; opt.textContent = ch;
        charSelect.appendChild(opt);
      });
    }
    if (tagSelect) {
      tagList.forEach(function(tg) {
        var opt = document.createElement('option');
        opt.value = tg; opt.textContent = tg;
        tagSelect.appendChild(opt);
      });
    }

    this.syncFilterUI();
  }

  /* ================================================================
     URL Hash — Read / Write / Listen
     ================================================================ */
  readHash() {
    var raw = window.location.hash.replace(/^#/, '');
    var params = new URLSearchParams(raw);
    var c = params.get(this.hashPrefix + 'character');
    var t = params.get(this.hashPrefix + 'tag');
    var s = params.get(this.hashPrefix + 'sort');
    var p = params.get(this.hashPrefix + 'page');

    if (c !== null) this.state.character = c;
    if (t !== null) this.state.tag = t;
    if (s === 'oldest' || s === 'newest' || s === 'random') this.state.sort = s;
    if (p !== null) {
      var pageNum = parseInt(p, 10);
      if (pageNum >= 1) this.state.page = pageNum;
    }
  }

  writeHash() {
    var params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (this.state.character) params.set(this.hashPrefix + 'character', this.state.character);
    else params.delete(this.hashPrefix + 'character');
    if (this.state.tag) params.set(this.hashPrefix + 'tag', this.state.tag);
    else params.delete(this.hashPrefix + 'tag');
    if (this.state.sort && this.state.sort !== 'random') params.set(this.hashPrefix + 'sort', this.state.sort);
    else params.delete(this.hashPrefix + 'sort');
    if (this.state.page > 1) params.set(this.hashPrefix + 'page', String(this.state.page));
    else params.delete(this.hashPrefix + 'page');

    var newHash = params.toString();
    var url = newHash
      ? window.location.pathname + window.location.search + '#' + newHash
      : window.location.pathname + window.location.search;

    if (window.location.hash !== '#' + newHash &&
        window.location.href.split('#')[0] + (newHash ? '#' + newHash : '') !== window.location.href) {
      history.replaceState(null, '', url);
    }
  }

  listenHashChange() {
    var self = this;
    window.addEventListener('hashchange', function() {
      var oc = self.state.character, ot = self.state.tag,
          os = self.state.sort, op = self.state.page;
      self.readHash();
      if (self.state.character !== oc || self.state.tag !== ot ||
          self.state.sort !== os || self.state.page !== op) {
        self.syncFilterUI();
        self.applyAll();
      }
    });
  }

  syncFilterUI() {
    if (!this.filterBar) return;
    var charSelect = this.filterBar.querySelector('[data-filter="character"]');
    var tagSelect  = this.filterBar.querySelector('[data-filter="tag"]');
    var sortSelect = this.filterBar.querySelector('[data-filter="sort"]');
    if (charSelect) charSelect.value = this.state.character;
    if (tagSelect)  tagSelect.value  = this.state.tag;
    if (sortSelect) sortSelect.value = this.state.sort;
  }

  /* ================================================================
     Filter Events
     ================================================================ */
  bindFilterEvents() {
    if (!this.filterBar) return;
    var self = this;
    var selects = this.filterBar.querySelectorAll('[data-filter]');
    selects.forEach(function(select) {
      select.addEventListener('change', function() {
        var ft = select.dataset.filter, v = select.value;
        if (ft === 'character') self.state.character = v;
        if (ft === 'tag')       self.state.tag       = v;
        if (ft === 'sort')      { self.state.sort = v; if (v === 'random') self.randomSeedVersion++; }
        self.state.page = 1;
        self.applyAll();
        self.writeHash();
      });
    });
  }

  /* ================================================================
     Card Dot Navigation (multi-image cards) — delegated
     ================================================================ */
  bindCardDots() {
    var self = this;
    this.masonryGrid.addEventListener('click', function(e) {
      var dot = e.target.closest('.gallery-card__dot');
      if (!dot) return;
      e.stopPropagation();
      var index = parseInt(dot.dataset.dotIndex, 10);
      if (isNaN(index)) return;
      var wrapper = dot.closest('.gallery-card__media-wrapper');
      if (!wrapper) return;
      self.switchCardImage(wrapper, index);
    });
  }

  switchCardImage(wrapper, index) {
    wrapper.querySelectorAll('.gallery-card__image').forEach(function(img, i) {
      img.classList.toggle('is-active', i === index);
    });
    wrapper.querySelectorAll('.gallery-card__dot').forEach(function(d, i) {
      d.classList.toggle('is-active', i === index);
    });
  }

  /* ================================================================
     Pagination Events (delegated)
     ================================================================ */
  bindPaginationEvents() {
    if (!this.paginationEl) return;
    var self = this;
    this.paginationEl.addEventListener('click', function(e) {
      var btn = e.target.closest('.gallery-pagination__btn');
      if (!btn || btn.classList.contains('is-disabled')) return;
      e.preventDefault();
      var page = parseInt(btn.dataset.page, 10);
      if (isNaN(page) || page < 1 || page > self.totalPages) return;
      self.state.page = page;
      self.applyAll();
      self.writeHash();
      self.masonryGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ================================================================
     Resize / Image load events
     ================================================================ */
  bindResizeEvents() {
    var self = this;
    window.addEventListener('resize', this.debounce(function() { self.refreshLayout(); }, 150));
    this.masonryGrid.querySelectorAll('img').forEach(function(img) {
      if (!img.complete) img.addEventListener('load', function() { self.refreshLayout(); });
    });
  }

  /* ================================================================
     Core Pipeline: Filter → Sort → Paginate → Render
     ================================================================ */
  applyAll() {
    var filtered = this.applyFilters();
    var sorted   = this.applySort(filtered);
    this.filteredCards = sorted;
    this.totalPages = Math.max(1, Math.ceil(sorted.length / this.pageSize));
    if (this.state.page > this.totalPages) this.state.page = this.totalPages;
    this.applyPagination(sorted);
    this.renderPagination();
    this.refreshLayout();
    this.observeVisibleVideos();
  }

  applyFilters() {
    var self = this;
    return this.cards.filter(function(card) {
      var cardChar = (card.dataset.character || '').trim();
      var cardTags = (card.dataset.tags || '').trim();

      if (self.state.character) {
        var charsArray = cardChar.split(',').map(function(t) { return t.trim(); }).filter(Boolean);
        if (!charsArray.includes(self.state.character)) return false;
      }
      if (self.state.tag) {
        var tagsArray = cardTags.split(',').map(function(t) { return t.trim(); }).filter(Boolean);
        if (!tagsArray.includes(self.state.tag)) return false;
      }
      return true;
    });
  }

  applySort(cards) {
    var sorted = cards.slice();
    var self = this;

    if (this.state.sort === 'random') {
      var key = this.state.character + '|' + this.state.tag + '|' + this.randomSeedVersion;
      if (key !== this.lastRandomKey) {
        for (var i = sorted.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var tmp = sorted[i]; sorted[i] = sorted[j]; sorted[j] = tmp;
        }
        this.lastRandomKey = key;
      }
    } else {
      var dir = this.state.sort === 'oldest' ? 1 : -1;
      sorted.sort(function(a, b) {
        return ((parseInt(a.dataset.date, 10) || 0) - (parseInt(b.dataset.date, 10) || 0)) * dir;
      });
    }

    // Reorder DOM physically
    var frag = document.createDocumentFragment();
    for (var k = 0; k < sorted.length; k++) {
      var card = sorted[k];
      if (card.parentNode === this.masonryGrid) card.remove();
      frag.appendChild(card);
    }
    this.masonryGrid.appendChild(frag);
    return sorted;
  }

  applyPagination(sorted) {
    var start = (this.state.page - 1) * this.pageSize;
    var end = start + this.pageSize;

    this.cards.forEach(function(card) { card.classList.add('is-hidden'); });

    sorted.forEach(function(card, index) {
      if (index >= start && index < end) card.classList.remove('is-hidden');
    });

    if (this.noResultsEl) {
      if (sorted.length === 0) this.noResultsEl.removeAttribute('hidden');
      else this.noResultsEl.setAttribute('hidden', '');
    }
  }

  renderPagination() {
    if (!this.paginationEl) return;
    var tp = this.totalPages, cp = this.state.page;

    if (tp <= 1) {
      this.paginationEl.innerHTML = '';
      this.paginationEl.style.display = 'none';
      return;
    }
    this.paginationEl.style.display = '';

    var pagesToShow = new Set();
    for (var i = 1; i <= Math.min(3, tp); i++) pagesToShow.add(i);
    for (var i = Math.max(1, tp - 1); i <= tp; i++) pagesToShow.add(i);
    pagesToShow.add(cp);
    if (cp > 1) pagesToShow.add(cp - 1);
    if (cp < tp) pagesToShow.add(cp + 1);

    var sorted = Array.from(pagesToShow).sort(function(a, b) { return a - b; });
    var fragments = [], prev = 0;
    for (var k = 0; k < sorted.length; k++) {
      var p = sorted[k];
      if (p - prev > 1) fragments.push({ type: 'ellipsis', key: 'e-' + prev + '-' + p });
      fragments.push({ type: 'page', key: 'p-' + p, num: p });
      prev = p;
    }

    var html = '';
    html += this.buildPageBtn('prev', '', cp > 1 ? cp - 1 : null, cp === 1,
      '<svg class="gallery-pagination__arrow" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 4L6 8l4 4"/></svg><span>Prev</span>');
    fragments.forEach(function(f) {
      if (f.type === 'ellipsis') html += '<span class="gallery-pagination__ellipsis">&hellip;</span>';
      else html += this.buildPageBtn('page', String(f.num), f.num, false, String(f.num));
    }.bind(this));
    html += this.buildPageBtn('next', '', cp < tp ? cp + 1 : null, cp === tp,
      '<span>Next</span><svg class="gallery-pagination__arrow" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 4l4 4-4 4"/></svg>');

    this.paginationEl.innerHTML = html;
  }

  buildPageBtn(type, ariaLabel, page, disabled, content) {
    var classes = ['gallery-pagination__btn', 'gallery-pagination__btn--' + type];
    if (page === this.state.page && type === 'page') classes.push('is-active');
    if (disabled) classes.push('is-disabled');
    var dataPage = page !== null ? ' data-page="' + page + '"' : '';
    var disabledAttr = disabled ? ' aria-disabled="true"' : '';
    var labelAttr = ariaLabel ? ' aria-label="' + ariaLabel + '"' : '';
    var currentAttr = (page === this.state.page && type === 'page') ? ' aria-current="page"' : '';
    return '<button type="button" class="' + classes.join(' ') + '"' + dataPage + disabledAttr + labelAttr + currentAttr + '>' + content + '</button>';
  }

  /* ================================================================
     Video Autoplay — IntersectionObserver
     ================================================================ */
  initVideoObserver() {
    if (!('IntersectionObserver' in window)) {
      this.masonryGrid.querySelectorAll('.gallery-card__video').forEach(function(v) { v.play().catch(function() {}); });
      return;
    }
    var self = this;
    this.videoObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        var video = entry.target;
        if (entry.isIntersecting) video.play().catch(function() {});
        else if (!video.paused) video.pause();
      });
    }, { threshold: 0.5 });
    this.observeVisibleVideos();
  }

  observeVisibleVideos() {
    if (!this.videoObserver) return;
    this.videoObserver.disconnect();
    this.cards.forEach(function(card) {
      if (!card.classList.contains('is-hidden')) {
        var video = card.querySelector('.gallery-card__video');
        if (video) this.videoObserver.observe(video);
      }
    }.bind(this));
  }

  /* ================================================================
     Modal — Bind Events
     ================================================================ */
  bindModalEvents() {
    if (!this.modal) return;
    var self = this;

    // Expand button — delegated on grid (handles new cards from API too)
    // Remove existing listeners by cloning...
    // Actually we re-call this method, so we need to be careful.
    // Use event delegation on the grid to handle ALL cards including API-added ones.
    this.masonryGrid.removeEventListener('click', this._modalExpandHandler);

    this._modalExpandHandler = function(e) {
      var btn = e.target.closest('[data-expand]');
      if (!btn) return;
      e.preventDefault();
      var card = btn.closest('.gallery-card');
      if (card) self.openModal(card);
    };
    this.masonryGrid.addEventListener('click', this._modalExpandHandler);

    // Close button
    var closeBtn = this.modal.querySelector('[data-modal-close]');
    if (closeBtn) closeBtn.addEventListener('click', function() { self.closeModal(); });

    // Product link → Quick View (global delegated listener, bound once)
    if (!this._qvClickBound) {
      var qvSelf = this;
      this._qvClickBound = function(e) {
        var link = e.target.closest('.gallery-detail__product-link');
        if (!link) return;
        e.preventDefault();
        e.stopPropagation();
        var handle = link.dataset.productHandle;
        if (handle) qvSelf.openProductQuickView(handle);
      };
      document.addEventListener('click', this._qvClickBound);
    }

    // Keyboard
    document.addEventListener('keydown', function(e) {
      if (self._fsOverlay) {
        if (e.key === 'Escape') { e.preventDefault(); self.closeFullscreen(); }
        return;
      }
      if (!self.modal.hasAttribute('active')) return;
      if (e.key === 'Escape') self.closeModal();
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); self.navigateModal(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.navigateModal(1); }
    });
  }

  /* ================================================================
     Modal — Open / Close / Navigate / Fullscreen
     ================================================================ */
  openModal(card) {
    if (!this.modal || !this.modalBody) return;

    var visibleCards = this.filteredCards.length > 0 ? this.filteredCards : this.getVisibleCards();
    this._modalCardIndex = visibleCards.indexOf(card);
    this._modalCards = visibleCards;

    this.buildModalContent(card);

    if (!this._overlayEl) {
      this._overlayEl = document.createElement('div');
      this._overlayEl.setAttribute('data-gallery-overlay', '');
      this._overlayEl.style.cssText =
        'position:fixed;top:0;left:0;width:100%;height:100%;z-index:48;' +
        'background:rgba(0,0,0,0.6);opacity:0;transition:opacity 0.35s ease;' +
        'cursor:url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2732%27 height=%2732%27 viewBox=%270 0 32 32%27%3E%3Ccircle cx=%2716%27 cy=%2716%27 r=%2715%27 fill=%27white%27/%3E%3Cline x1=%2710%27 y1=%2710%27 x2=%2722%27 y2=%2722%27 stroke=%27black%27 stroke-width=%272.5%27 stroke-linecap=%27round%27/%3E%3Cline x1=%2722%27 y1=%2710%27 x2=%2710%27 y2=%2722%27 stroke=%27black%27 stroke-width=%272.5%27 stroke-linecap=%27round%27/%3E%3C/svg%3E") 16 16, pointer;';
      this._overlayEl.addEventListener('click', this._closeModalBound || (this._closeModalBound = this.closeModal.bind(this)));
      document.body.appendChild(this._overlayEl);
    }
    this._overlayEl.style.display = 'block';
    this._overlayEl.offsetHeight;
    this._overlayEl.style.opacity = '1';

    if (this.currentOpenCard) {
      var prevBtn = this.currentOpenCard.querySelector('[data-expand]');
      if (prevBtn) prevBtn.removeAttribute('aria-expanded');
    }

    this.modal.removeAttribute('hidden');
    this.modal.setAttribute('active', '');
    document.body.style.overflow = 'hidden';

    var expandBtn = card.querySelector('[data-expand]');
    if (expandBtn) expandBtn.setAttribute('aria-expanded', 'true');
    this.currentOpenCard = card;
  }

  buildModalContent(card) {
    var template = card.querySelector('.gallery-card__detail-template');
    if (!template) return;

    var mediaUrl    = template.dataset.mediaUrl || '';
    var mediaType   = template.dataset.mediaType || '';
    var videoPoster = template.dataset.videoPoster || '';
    var textContent = template.content.cloneNode(true);

    this.modalBody.innerHTML = '';

    var mediaArea  = document.createElement('div');
    mediaArea.className = 'gallery-modal__media-area';

    var mediaFrame = document.createElement('div');
    mediaFrame.className = 'gallery-modal__media-frame';

    var self = this;

    if (mediaType === 'video' && mediaUrl) {
      var video = document.createElement('video');
      video.className = 'gallery-modal__media-video';
      video.src = mediaUrl; video.muted = true; video.loop = true;
      video.playsInline = true; video.autoplay = true;
      if (videoPoster) video.poster = videoPoster;
      mediaFrame.appendChild(video);
      video.addEventListener('loadedmetadata', function() {
        self.fitFrameToMedia(mediaFrame, mediaType, video);
      }, { once: true });
    } else if (mediaType === 'image' && mediaUrl) {
      var mediaUrls = template.dataset.mediaUrls || '';
      var picCount = parseInt(template.dataset.pictureCount, 10) || 0;
      var allUrls = picCount > 1 && mediaUrls ? mediaUrls.split(',') : [mediaUrl];

      allUrls.forEach(function(url, idx) {
        var img = document.createElement('img');
        img.className = 'gallery-modal__media-image';
        if (idx === 0) img.classList.add('is-active');
        img.src = url.trim(); img.alt = '';
        img.dataset.modalImgIndex = idx;
        mediaFrame.appendChild(img);
      });

      var firstImg = mediaFrame.querySelector('img');
      if (firstImg) {
        if (firstImg.complete && firstImg.naturalWidth) self.fitFrameToMedia(mediaFrame, mediaType, firstImg);
        else firstImg.addEventListener('load', function() { self.fitFrameToMedia(mediaFrame, mediaType, firstImg); }, { once: true });
      }

      if (allUrls.length > 1) {
        var dots = document.createElement('div');
        dots.className = 'gallery-modal__dots';
        dots.dataset.modalDots = '';
        allUrls.forEach(function(_, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'gallery-modal__dot' + (i === 0 ? ' is-active' : '');
          dot.dataset.dotIndex = i;
          dot.setAttribute('aria-label', 'Image ' + (i + 1));
          dot.addEventListener('click', function(e) { e.stopPropagation(); self.switchModalImage(mediaFrame, i); });
          dots.appendChild(dot);
        });
        mediaFrame.appendChild(dots);
      }
    }

    // Fullscreen button
    var fsBtn = document.createElement('button');
    fsBtn.type = 'button';
    fsBtn.className = 'gallery-modal__fullscreen-btn';
    fsBtn.setAttribute('aria-label', 'View fullscreen');
    fsBtn.innerHTML = '<svg class="gallery-modal__fullscreen-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M16.5 16.5L21 21"/><path d="M8 11h6M11 8v6"/></svg>';
    fsBtn.addEventListener('click', function(e) { e.stopPropagation(); self.toggleFullscreen(mediaFrame); });
    mediaFrame.appendChild(fsBtn);

    // Prev
    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'gallery-modal__nav gallery-modal__nav--prev';
    prevBtn.setAttribute('aria-label', 'Previous');
    prevBtn.innerHTML = '<svg class="gallery-modal__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>';
    prevBtn.addEventListener('click', function(e) { e.stopPropagation(); self.navigateModal(-1); });
    mediaFrame.appendChild(prevBtn);

    // Next
    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'gallery-modal__nav gallery-modal__nav--next';
    nextBtn.setAttribute('aria-label', 'Next');
    nextBtn.innerHTML = '<svg class="gallery-modal__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';
    nextBtn.addEventListener('click', function(e) { e.stopPropagation(); self.navigateModal(1); });
    mediaFrame.appendChild(nextBtn);

    mediaArea.appendChild(mediaFrame);
    this.modalBody.appendChild(mediaArea);

    var textArea = document.createElement('div');
    textArea.className = 'gallery-modal__text-area';
    textArea.appendChild(textContent);
    this.modalBody.appendChild(textArea);

    this.updateNavButtons();
  }

  fitFrameToMedia(frame, mediaType, mediaEl) {
    if (window.innerWidth >= 768) return;
    var nw, nh;
    if (mediaType === 'image') { nw = mediaEl.naturalWidth; nh = mediaEl.naturalHeight; }
    else { nw = mediaEl.videoWidth; nh = mediaEl.videoHeight; }
    if (!nw || !nh) return;
    var fw = frame.clientWidth || frame.offsetWidth;
    if (!fw) return;
    var h = fw * (nh / nw);
    frame.style.height = Math.min(h, window.innerHeight * 0.5) + 'px';
  }

  switchModalImage(mediaFrame, index) {
    mediaFrame.querySelectorAll('.gallery-modal__media-image').forEach(function(img, i) { img.classList.toggle('is-active', i === index); });
    mediaFrame.querySelectorAll('.gallery-modal__dot').forEach(function(d, i) { d.classList.toggle('is-active', i === index); });
  }

  navigateModal(dir) {
    if (!this._modalCards || this._modalCards.length === 0) return;
    var newIndex = this._modalCardIndex + dir;
    if (newIndex < 0 || newIndex >= this._modalCards.length) return;
    this._modalCardIndex = newIndex;
    var card = this._modalCards[newIndex];
    if (this.currentOpenCard) {
      var oldBtn = this.currentOpenCard.querySelector('[data-expand]');
      if (oldBtn) oldBtn.removeAttribute('aria-expanded');
    }
    this.currentOpenCard = card;
    var newBtn = card.querySelector('[data-expand]');
    if (newBtn) newBtn.setAttribute('aria-expanded', 'true');
    this.buildModalContent(card);
  }

  updateNavButtons() {
    if (!this.modalBody) return;
    var prevBtn = this.modalBody.querySelector('.gallery-modal__nav--prev');
    var nextBtn = this.modalBody.querySelector('.gallery-modal__nav--next');
    if (prevBtn) { if (this._modalCardIndex <= 0) prevBtn.setAttribute('disabled', ''); else prevBtn.removeAttribute('disabled'); }
    if (nextBtn) { if (this._modalCardIndex >= (this._modalCards?.length || 0) - 1) nextBtn.setAttribute('disabled', ''); else nextBtn.removeAttribute('disabled'); }
  }

  toggleFullscreen(mediaFrame) {
    if (this._fsOverlay) { this.closeFullscreen(); return; }
    var mediaEl = mediaFrame.querySelector('.gallery-modal__media-image.is-active, video');
    if (!mediaEl) return;

    var overlay = document.createElement('div');
    overlay.className = 'gallery-fullscreen-overlay';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button'; closeBtn.className = 'gallery-fullscreen__close';
    closeBtn.setAttribute('aria-label', 'Close fullscreen');
    closeBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="28" height="28"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    closeBtn.addEventListener('click', this._fsCloseBound || (this._fsCloseBound = this.closeFullscreen.bind(this)));

    var escHint = document.createElement('p');
    escHint.className = 'gallery-fullscreen__hint';
    escHint.textContent = 'Press ESC to exit fullscreen';

    var clone = mediaEl.cloneNode(true);
    clone.classList.add('gallery-fullscreen__media');
    if (clone.tagName === 'VIDEO') {
      clone.controls = true; clone.muted = false; clone.loop = false; clone.playsInline = false;
      if (mediaEl.currentTime) clone.currentTime = mediaEl.currentTime;
      clone.play().catch(function() {});
    }
    overlay.appendChild(closeBtn);
    overlay.appendChild(escHint);
    overlay.appendChild(clone);
    document.body.appendChild(overlay);

    this._prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this._fsOverlay = overlay;

    var self = this;
    this._fsEscHandler = function(e) { if (e.key === 'Escape') { e.preventDefault(); self.closeFullscreen(); } };
    document.addEventListener('keydown', this._fsEscHandler);
  }

  closeFullscreen() {
    if (!this._fsOverlay) return;
    if (this._fsOverlay.parentNode) this._fsOverlay.remove();
    this._fsOverlay = null;
    document.body.style.overflow = this._prevOverflow || '';
    if (this._fsEscHandler) { document.removeEventListener('keydown', this._fsEscHandler); this._fsEscHandler = null; }
  }

  getVisibleCards() {
    return this.cards.filter(function(card) { return !card.classList.contains('is-hidden'); });
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.removeAttribute('active');
    this.modal.setAttribute('hidden', '');
    if (this._overlayEl) {
      this._overlayEl.style.opacity = '0';
      var self = this;
      setTimeout(function() {
        if (self._overlayEl && !self.modal.hasAttribute('active')) self._overlayEl.style.display = 'none';
      }, 350);
    }
    document.body.style.overflow = '';
    if (this.currentOpenCard) {
      var btn = this.currentOpenCard.querySelector('[data-expand]');
      if (btn) btn.removeAttribute('aria-expanded');
      this.currentOpenCard = null;
    }
    var self2 = this;
    setTimeout(function() {
      if (!self2.modal.hasAttribute('active') && self2.modalBody) self2.modalBody.innerHTML = '';
    }, 350);
  }

  /* ================================================================
     Product Quick View — same HTML structure as floating-product-bundle
     Creates a <quick-view> Web Component matching the theme's drawer
     ================================================================ */
  _ensureQVContainer() {
    if (this._qvContainer) return;

    var qvHTML = '';
    qvHTML += '<quick-view id="qv-gallery-temp" class="qv-gallery-panel quick-view x-modal drawer fixed bottom-0 left-0 h-full w-full pointer-events-none" style="z-index:60"';
    qvHTML += ' role="dialog" aria-modal="true" data-product-url="">';
    qvHTML += '<overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" style="z-index:60"';
    qvHTML += ' aria-controls="qv-gallery-temp" aria-expanded="false"></overlay-element>';
    qvHTML += '<div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">';
    qvHTML += '<gesture-element class="drawer__header flex justify-between opacity-0 invisible relative">';
    qvHTML += '<button class="button button--secondary button--close drawer__close z-1 absolute top-0 right-0 flex items-center justify-center opacity-0"';
    qvHTML += ' type="button" is="hover-button" aria-controls="qv-gallery-temp" aria-expanded="false" aria-label="Close">';
    qvHTML += '<span class="btn-fill" data-fill></span>';
    qvHTML += '<span class="btn-text"><svg viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="1.5rem" height="1.5rem"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span>';
    qvHTML += '</button>';
    qvHTML += '</gesture-element>';
    qvHTML += '<div class="quick-view__content drawer__content opacity-0 invisible flex flex-col h-full grow shrink"></div>';
    qvHTML += '</div>';
    qvHTML += '</quick-view>';

    // Hidden trigger button — the theme's <quick-view> Web Component uses this
    qvHTML += '<button id="qv-gallery-trigger" style="position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none"';
    qvHTML += ' aria-controls="qv-gallery-temp" aria-expanded="false"></button>';

    document.body.insertAdjacentHTML('beforeend', qvHTML);

    // Force Quick View title → price stacked vertically (wide screen fix)
    if (!document.getElementById('qv-gallery-styles')) {
      var qvStyle = document.createElement('style');
      qvStyle.id = 'qv-gallery-styles';
      qvStyle.textContent =
        '.qv-gallery-panel .product__title{display:flex!important;flex-direction:column!important;align-items:flex-start!important}';
      document.head.appendChild(qvStyle);
    }

    // Store references
    this._qvContainer = document.getElementById('qv-gallery-temp');
    this._qvOverlayEl = this._qvContainer.querySelector('overlay-element');
    this._qvContent = this._qvContainer.querySelector('.quick-view__content');
    this._qvTriggerBtn = document.getElementById('qv-gallery-trigger');

    // Close on overlay click
    var self = this;
    this._qvOverlayEl.addEventListener('click', function(e) {
      if (e.target === self._qvOverlayEl) self.closeProductQuickView();
    });

    // Close button
    var closeBtn = this._qvContainer.querySelector('.drawer__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function() { self.closeProductQuickView(); });
    }
  }

  openProductQuickView(handle) {
    if (!handle) return;

    // DO NOT close gallery modal — Quick View opens on top of it (z-index:60 > 50)
    this._ensureQVContainer();

    var productUrl = '/products/' + handle;
    var qvId = 'Quickview-gallery-' + handle.replace(/[^a-z0-9-]/gi, '');

    // Update container identity
    this._qvContainer.id = qvId;
    this._qvContainer.setAttribute('data-product-url', productUrl);
    this._qvContainer.setAttribute('aria-label', 'Quick view');

    // --- Activate quick-view: only fix pointer-events + overlay visibility ---
    // Let the Web Component handle all layout/content/drawer CSS
    this._qvContainer.classList.remove('pointer-events-none');
    this._qvContainer.classList.add('pointer-events-auto');

    this._qvOverlayEl.classList.remove('invisible', 'opacity-0', 'pointer-events-none');
    this._qvOverlayEl.classList.add('pointer-events-auto');
    this._qvOverlayEl.setAttribute('aria-expanded', 'true');

    // Update aria-controls references
    this._qvOverlayEl.setAttribute('aria-controls', qvId);
    this._qvTriggerBtn.id = 'qv-trigger-' + handle.replace(/[^a-z0-9-]/gi, '');
    this._qvTriggerBtn.setAttribute('aria-controls', qvId);

    var closeBtn = this._qvContainer.querySelector('.drawer__close');
    if (closeBtn) {
      closeBtn.setAttribute('aria-controls', qvId);
      closeBtn.setAttribute('aria-expanded', 'true');
    }

    // Raise drawer__inner above overlay (overlay z:60 → inner z:61)
    var drawerInner = this._qvContainer.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.style.zIndex = '61';

    // Click hidden trigger to tell <quick-view> Web Component to open
    this._qvTriggerBtn.click();
  }

  closeProductQuickView() {
    if (!this._qvContainer) return;

    // Restore pointer-events-none / invisible — Web Component handles the rest
    this._qvContainer.classList.add('pointer-events-none');
    this._qvContainer.classList.remove('pointer-events-auto');

    this._qvOverlayEl.classList.add('invisible', 'opacity-0', 'pointer-events-none');
    this._qvOverlayEl.classList.remove('pointer-events-auto');
    this._qvOverlayEl.setAttribute('aria-expanded', 'false');

    var drawerInner = this._qvContainer.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.style.zIndex = '';

    var closeBtn = this._qvContainer.querySelector('.drawer__close');
    if (closeBtn) closeBtn.setAttribute('aria-expanded', 'false');
  }

  /* ================================================================
     Grid Row Recalculation (CSS Grid masonry)
     ================================================================ */
  recalcGridRows() {
    if (!this.masonryGrid) return;
    var style = getComputedStyle(this.masonryGrid);
    var colTpl = style.gridTemplateColumns;
    var colCount = colTpl.split(' ').length;
    if (colCount < 2) return;

    var gap     = parseFloat(style.gap) || parseFloat(style.columnGap) || 0;
    var rowGap  = parseFloat(style.rowGap) || parseFloat(style.gap) || 0;
    var trackSize = 1 + rowGap;
    var gridWidth = this.masonryGrid.clientWidth;
    if (gridWidth === 0) return;
    var colWidth = (gridWidth - gap * (colCount - 1)) / colCount;

    var calcSpan = function(cardHeight) { return Math.max(1, Math.ceil((cardHeight + rowGap) / trackSize)); };

    this.cards.forEach(function(card) {
      var wrapper = card.querySelector('.gallery-card__media-wrapper');
      if (!wrapper) return;
      var inline = wrapper.getAttribute('style') || '';
      var m = inline.match(/aspect-ratio:\s*(\d+)\s*\/\s*(\d+)/);
      if (!m) return;
      var w = parseFloat(m[1]), h = parseFloat(m[2]);
      var ratio = w / h;
      var isVideo = !!card.querySelector('.gallery-card__video');
      var isWide = isVideo && ratio > 1;

      if (isWide) {
        card.classList.add('gallery-card--span-2');
        card.style.gridRowEnd = 'span ' + calcSpan((colWidth * 2 + gap) / ratio);
      } else {
        card.classList.remove('gallery-card--span-2');
        card.style.gridRowEnd = 'span ' + calcSpan(colWidth / ratio);
      }
    });
  }

  refreshLayout() { this.recalcGridRows(); }

  debounce(fn, delay) {
    var timer;
    return function() {
      var args = arguments, self = this;
      clearTimeout(timer);
      timer = setTimeout(function() { fn.apply(self, args); }, delay);
    };
  }

  /* ================================================================
     Static: Auto-init all instances
     ================================================================ */
  static initAll() {
    document.querySelectorAll('.gallery-masonry').forEach(function(el) {
      if (!el._galleryInstance) el._galleryInstance = new GalleryMasonrySection(el);
    });
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() { GalleryMasonrySection.initAll(); });
} else {
  GalleryMasonrySection.initAll();
}

// Shopify Theme Editor re-init
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(e) {
    var section = document.getElementById('shopify-section-' + e.detail.sectionId);
    if (!section) return;
    var el = section.querySelector('.gallery-masonry');
    if (el && !el._galleryInstance) el._galleryInstance = new GalleryMasonrySection(el);
  });
  document.addEventListener('shopify:section:unload', function(e) {
    var section = document.getElementById('shopify-section-' + e.detail.sectionId);
    if (!section) return;
    var el = section.querySelector('.gallery-masonry');
    if (el && el._galleryInstance) {
      if (el._galleryInstance.videoObserver) el._galleryInstance.videoObserver.disconnect();
      el._galleryInstance = null;
    }
  });
  document.addEventListener('shopify:block:select', function(e) {
    var section = document.getElementById('shopify-section-' + e.detail.sectionId);
    if (!section) return;
    var el = section.querySelector('.gallery-masonry');
    if (el && el._galleryInstance) requestAnimationFrame(function() { el._galleryInstance.refreshLayout(); });
  });
}
